import { print } from 'graphql';
import { useMutation, useQueryClient } from 'react-query';
import {
  createCustomFieldSection as createSectionQuery,
  updateCustomFieldSection as updateSectionQuery,
  deleteCustomFieldSection as deleteSectionQuery,
  reorderCustomFieldSections as reorderSectionsQuery,
} from 'src/graphql/customFields/customFieldSections';
import { fetcher } from 'src/utils/fetch';

export interface CustomFieldSection {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  displayOrder: number;
  fieldCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateSectionInput {
  name: string;
  description?: string | null;
  isDefault?: boolean;
}

interface UpdateSectionInput {
  id: string;
  name?: string;
  description?: string | null;
  isDefault?: boolean;
}

interface SectionOrderInput {
  id: string;
  displayOrder: number;
}

interface ReorderSectionsInput {
  orders: SectionOrderInput[];
}

interface ReorderSectionsResponse {
  reorderCustomFieldSections: { sections: CustomFieldSection[] };
}

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
  // A section rename or reassignment changes the field list's Section column.
  queryClient.invalidateQueries(['GetOrganisationCustomFields']);
};

export const useCustomFieldSectionMutations = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: createSection, isLoading: isCreating } = useMutation<
    { createCustomFieldSection: CustomFieldSection },
    Error,
    CreateSectionInput
  >(
    async (input) => {
      const fetcherFn = fetcher<
        { createCustomFieldSection: CustomFieldSection },
        { input: CreateSectionInput }
      >(print(createSectionQuery), { input });
      return fetcherFn();
    },
    { onSuccess: () => invalidate(queryClient) },
  );

  const { mutateAsync: updateSection, isLoading: isUpdating } = useMutation<
    { updateCustomFieldSection: CustomFieldSection },
    Error,
    UpdateSectionInput
  >(
    async (input) => {
      const fetcherFn = fetcher<
        { updateCustomFieldSection: CustomFieldSection },
        { input: UpdateSectionInput }
      >(print(updateSectionQuery), { input });
      return fetcherFn();
    },
    { onSuccess: () => invalidate(queryClient) },
  );

  const { mutateAsync: deleteSection, isLoading: isDeleting } = useMutation<
    { deleteCustomFieldSection: { id: string; deleted: boolean } },
    Error,
    { id: string }
  >(
    async (input) => {
      const fetcherFn = fetcher<
        { deleteCustomFieldSection: { id: string; deleted: boolean } },
        { input: { id: string } }
      >(print(deleteSectionQuery), { input });
      return fetcherFn();
    },
    { onSuccess: () => invalidate(queryClient) },
  );

  const { mutateAsync: reorderSections, isLoading: isReordering } = useMutation<
    ReorderSectionsResponse,
    Error,
    ReorderSectionsInput
  >(
    async (input) => {
      const fetcherFn = fetcher<
        ReorderSectionsResponse,
        { input: ReorderSectionsInput }
      >(print(reorderSectionsQuery), { input });
      return fetcherFn();
    },
    { onSuccess: () => invalidate(queryClient) },
  );

  return {
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    isCreating,
    isUpdating,
    isDeleting,
    isReordering,
    isLoading: isCreating || isUpdating || isDeleting || isReordering,
  };
};
